/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState } from "react";
import { DatePicker, DatePickerChangeTypeConstants } from "components/date-picker/DatePicker";
import { HelpText } from "components/help-text/HelpText";
import { css } from "@emotion/react";
import { isInvalidValidDate } from "Utilities";
import { usePersonalization } from "contexts/PersonalizationContext";

const styles = {
  dateSelector: css`
    vertical-align: top;
    display: inline-block;
  
    fieldset {
      border: solid black 1px;
      padding: 0;
      padding-left: 10px;
      padding-right: 10px;
      padding-bottom: 10px;
      display: inline;
      margin-left: 4px;
      margin-right: 4px;
  
      legend {
        width: auto;
        margin: 0;
        margin-left: 4px;
        padding-left: 4px;
        padding-right: 4px;
  
        &.clickable {
          cursor: pointer;
        }
      }
    }`
};

export const DatesSelector = ( { fromDate, toDate, helpId, onChangeFromDate, onChangeToDate }: { fromDate?: number; toDate?: number; helpId: string; onChangeFromDate: ( fromDate: number ) => void; onChangeToDate: ( toDate: number ) => void; } ): JSX.Element => {
  const personalization = usePersonalization();

  const fromDateValue = useMemo( () => !fromDate || isInvalidValidDate( fromDate ) ? new Date( 2020, 0, 1, 0, 0, 0 ) : new Date( fromDate ), [ fromDate ] );
  const toDateValue = useMemo( () => !toDate || isInvalidValidDate( toDate ) ? new Date( 2020, 0, 1, 0, 0, 0 ) : new Date( toDate ), [ toDate ] );
  const year = useMemo( () => fromDateValue.getFullYear(), [ fromDateValue ] );
  const fromMonth = useMemo( () => fromDateValue.getMonth(), [ fromDateValue ] );

  const [ calendarViewSelectedYear, setCalendarViewSelectedYear ] = useState( year );
  const [ calendarViewSelectedMonth, setCalendarViewSelectedMonth ] = useState( fromMonth );

  useEffect( () => {
    setCalendarViewSelectedMonth( fromMonth );
    setCalendarViewSelectedYear( year );
  }, [ fromMonth, year ] );

  const monthRange = useMemo( () => ( { fromDate: new Date( calendarViewSelectedYear, calendarViewSelectedMonth, 1, 0, 0, 0 ), toDate: new Date( calendarViewSelectedYear, calendarViewSelectedMonth + 1, 0, 0, 0, 0 ) } ), [ calendarViewSelectedMonth, calendarViewSelectedYear ] );
  const q1Range = useMemo( () => ( { fromDate: new Date( calendarViewSelectedYear, 0, 1, 0, 0, 0 ), toDate: new Date( calendarViewSelectedYear, 3, 0 ) } ), [ calendarViewSelectedYear ] );
  const q2Range = useMemo( () => ( { fromDate: new Date( calendarViewSelectedYear, 3, 1, 0, 0, 0 ), toDate: new Date( calendarViewSelectedYear, 6, 0 ) } ), [ calendarViewSelectedYear ] );
  const q3Range = useMemo( () => ( { fromDate: new Date( calendarViewSelectedYear, 6, 1, 0, 0, 0 ), toDate: new Date( calendarViewSelectedYear, 9, 0 ) } ), [ calendarViewSelectedYear ] );
  const q4Range = useMemo( () => ( { fromDate: new Date( calendarViewSelectedYear, 9, 1, 0, 0, 0 ), toDate: new Date( calendarViewSelectedYear, 12, 0 ) } ), [ calendarViewSelectedYear ] );
  const yearRange = useMemo( () => ( { fromDate: new Date( calendarViewSelectedYear, 0, 1, 0, 0, 0 ), toDate: new Date( calendarViewSelectedYear + 1, 0, 0, 0, 0, 0 ) } ), [ calendarViewSelectedYear ] );

  return ( <>
    <div css={ styles.dateSelector }>
      <fieldset>
        <legend><label>Dates <HelpText helpId={ helpId } /></label></legend>
        <div css={ css`display:flex;` }>
          <DatePicker key={ `from${ fromDateValue.valueOf() }` } currentDate={ fromDateValue.valueOf() } displayFormat="dd MMM yyyy" onChangeDate={ ( dt, datePickerChangeType ) => { onChangeFromDate( dt ); personalization.syncCalendar && datePickerChangeType === DatePickerChangeTypeConstants.mouseClick && onChangeToDate( dt ); } } onCalendarViewYearChange={ ( year: number ) => setCalendarViewSelectedYear( year ) } onCalendarViewMonthChange={ ( month: number ) => setCalendarViewSelectedMonth( month - 1 ) } >
            <button type="button" onClick={ () => { onChangeFromDate( monthRange.fromDate.valueOf() ); onChangeToDate( monthRange.toDate.valueOf() ); } }>Month</button>
            <button type="button" onClick={ () => { onChangeFromDate( q1Range.fromDate.valueOf() ); onChangeToDate( q1Range.toDate.valueOf() ); } }>Q1</button>
            <button type="button" onClick={ () => { onChangeFromDate( q2Range.fromDate.valueOf() ); onChangeToDate( q2Range.toDate.valueOf() ); } }>Q2</button>
            <button type="button" onClick={ () => { onChangeFromDate( q3Range.fromDate.valueOf() ); onChangeToDate( q3Range.toDate.valueOf() ); } }>Q3</button>
            <button type="button" onClick={ () => { onChangeFromDate( q4Range.fromDate.valueOf() ); onChangeToDate( q4Range.toDate.valueOf() ); } }>Q4</button>
            <button type="button" onClick={ () => { onChangeFromDate( yearRange.fromDate.valueOf() ); onChangeToDate( yearRange.toDate.valueOf() ); } }>Year</button>
          </DatePicker>
          <DatePicker key={ `to${ toDateValue.valueOf() }` } currentDate={ toDateValue.valueOf() } displayFormat="dd MMM yyyy" onChangeDate={ ( dt, _ ) => { onChangeToDate( dt ); } } />
        </div>
      </fieldset>
    </div>
  </> );
};
